import { useQuery } from "@apollo/client";
import { CurrentUserQuery, FieldState } from "_graphql-types-frontend/graphql";
import { CURRENT_USER_QUERY } from "./graphql";

type CurrentUser = CurrentUserQuery["currentUser"] & {
  writeDraftState: FieldState.Draft | FieldState.MgrDraft;
  writePublishState: null | FieldState.Mgr;
  clientView: boolean;
};

/**
 * Returns the current user from Apollo cache, or null if not yet loaded.
 * Uses useQuery with cache-only policy so the component reactively
 * re-renders when UserLoader populates the cache.
 */
export function useOptionalCurrentUser(): CurrentUser | null {
  const { data } = useQuery<{ currentUser: CurrentUserQuery["currentUser"] }>(
    CURRENT_USER_QUERY,
    { fetchPolicy: "cache-only" }
  );

  const currentUser = data?.currentUser;
  if (!currentUser) return null;

  const clientView =
    sessionStorage.getItem("clientView") === "true" &&
    currentUser.flags.isSubscriptionUser;

  return {
    writeDraftState: currentUser.flags.isSubscriptionUser
      ? FieldState.Draft
      : FieldState.MgrDraft,
    writePublishState: currentUser.flags.isSubscriptionUser
      ? null
      : FieldState.Mgr,
    clientView: currentUser.flags.isClientUser || clientView,
    ...currentUser,
    ...(clientView && {
      flags: {
        ...currentUser.flags,
        isSubscriptionUser: false,
        isClientUser: true,
      },
    }),
  };
}

/**
 * Returns the current user, throwing if not available.
 * Only use in components guaranteed to be behind UserLoader.
 */
export function useCurrentUser(): CurrentUser {
  const user = useOptionalCurrentUser();
  if (!user) throw new Error("no current user");
  return user;
}
